import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Copy, Download, Send } from "lucide-react";

/**
 * View a ticket confirmation in HTML preview, copy the plaintext, download the .html file,
 * or fire "Resend to customer via Resend". Lets admin also just email the same content from
 * their personal company email if they need to.
 */
export default function ConfirmationViewerDialog({ open, onOpenChange, ticketId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (!open || !ticketId) return;
    setLoading(true);
    api.get(`/admin/tickets/${ticketId}/confirmation`)
      .then(r => setData(r.data))
      .catch(err => toast.error(err?.response?.data?.detail || "Could not load confirmation"))
      .finally(() => setLoading(false));
  }, [open, ticketId]);

  const copyPlain = async () => {
    try {
      await navigator.clipboard.writeText(data.plaintext);
      toast.success("Plaintext copied to clipboard");
    } catch { toast.error("Copy failed — select and copy manually from the preview"); }
  };

  const copyHtml = async () => {
    try {
      await navigator.clipboard.writeText(data.html);
      toast.success("HTML copied to clipboard");
    } catch { toast.error("Copy failed"); }
  };

  const downloadHtml = () => {
    const blob = new Blob([data.html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `confirmation_${ticketId}.html`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const resend = async () => {
    setResending(true);
    try {
      const { data: r } = await api.post(`/admin/tickets/${ticketId}/resend-confirmation`);
      toast.success(`Sent to ${r.sent_to}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    }
    setResending(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Ticket confirmation</DialogTitle>
          <DialogDescription className="text-sm text-neutral-500">
            {loading ? "Loading…" : <>To <span className="font-medium text-black">{data?.to_name}</span> · {data?.to_email} · <span className="font-mono">{data?.subject}</span></>}
          </DialogDescription>
        </DialogHeader>

        {!loading && data && (
          <>
            <div className="flex flex-wrap gap-2 pb-3 border-b border-neutral-200">
              <Button size="sm" variant="outline" onClick={copyPlain} data-testid="viewer-copy-plaintext"><Copy size={12} className="mr-2" /> Copy as plain text</Button>
              <Button size="sm" variant="outline" onClick={copyHtml} data-testid="viewer-copy-html"><Copy size={12} className="mr-2" /> Copy HTML</Button>
              <Button size="sm" variant="outline" onClick={downloadHtml} data-testid="viewer-download-html"><Download size={12} className="mr-2" /> Download .html</Button>
              <Button size="sm" onClick={resend} disabled={resending} className="ml-auto bg-black text-white rounded-full" data-testid="viewer-resend-btn"><Send size={12} className="mr-2" /> {resending ? "Resending…" : "Resend to customer (with QR)"}</Button>
            </div>
            <div className="flex-1 overflow-y-auto mt-3 rounded-xl border border-neutral-200 bg-white">
              <iframe title="Confirmation preview" srcDoc={data.html} className="w-full h-[500px] rounded-xl" sandbox="allow-popups" data-testid="viewer-preview-iframe" />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
